import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useCreatePricingPlan } from 'src/features/pricingPlan/api/useCreatePricingPlan';
import { LIST_PRICING_PLANS_RESOURCE } from 'src/features/pricingPlan/api/useListPricingPlans';
import PricingPlanFormModal from 'src/features/pricingPlan/components/form/PricingPlanFormModal';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { PATH } from 'src/features/navigation/utils/constants';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan?: PricingPlan;
    children?: React.ReactNode;
}

function CreatePricingPlanFormModal<TData>({ className, semantics }: PricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const { mutate, isPending } = useCreatePricingPlan({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.createPricingPlanSuccess),
                description: formatMessage(pricingPlanMessages.createPricingPlanSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
            // Only on create: land the user on the new plan's dashboard.
            void navigate(`/${PATH.ADMINISTRATION}/pricing-plan/${success.id}`);
        },
    });

    return (
        <PricingPlanFormModal
            className={className}
            mutate={(values, onSuccess, onError) => mutate({ ...values, parkingId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.addPricingPlanSubtitle)}
        />
    );
}

export default CreatePricingPlanFormModal;
